// Button (design-system audit §4b): the boxed control. One filled primary per place (Book a visit),
// secondary boxed buttons beside it, danger for what can't be undone. 34 px tall (DESIGN.md §8), 26 small,
// 44 on a touch screen; touch-on-phone is 34 at desktop widths and 44 at phone widths (a side panel's
// actions, which a phone user taps). Hover underlines the label; busy draws the frame dashed (waiting, §2). Behaviour
// (press, keyboard, focus ring only for the keyboard) is React Aria's; the look reads only --btn-*.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Glyph, type GlyphName } from '../Glyph/Glyph.tsx';
import './Button.css';

export interface ButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'touch' | 'touch-on-phone';
  /** A glyph before the label (Filter's funnel). */
  icon?: GlyphName;
  isDisabled?: boolean;
  /** Working on it: the label stays, the frame is dashed, presses wait. */
  isBusy?: boolean;
  onPress?: () => void;
  type?: 'button' | 'submit' | 'reset';
  /** A glyph after the label (a menu's chevron). */
  iconEnd?: GlyphName;
  /** For a button that opens something (a pop-over): its expanded state. */
  'aria-expanded'?: boolean;
  /** For a button that opens a menu: 'menu', and the id of the menu it opens. */
  'aria-haspopup'?: 'menu' | 'dialog' | 'listbox';
  'aria-controls'?: string;
  /** What says more about it (Close September: what closing does, or what stops it). */
  'aria-describedby'?: string;
}

export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  icon,
  iconEnd,
  isDisabled,
  isBusy,
  onPress,
  type = 'button',
  ...aria
}: ButtonProps) {
  const id = useId();
  const wasBusy = useRef(false);
  if (isBusy) wasBusy.current = true;
  // a busy button takes what it said aloud with it when it goes (QA-744)
  useEffect(
    () => () => {
      if (!wasBusy.current || typeof document === 'undefined') return;
      dropAnnouncements(document, id);
      // the first thing said on a page is added a moment late (React Aria waits 100 ms)
      setTimeout(() => dropAnnouncements(document, id), 150);
    },
    [id],
  );
  return (
    <AriaButton
      {...aria}
      id={id}
      type={type}
      className="fl-btn"
      data-variant={variant}
      data-size={size}
      isDisabled={isDisabled}
      isPending={isBusy}
      onPress={onPress}
    >
      {icon ? <Glyph name={icon} className="fl-btn__glyph" /> : null}
      <span className="fl-btn__label">{children}</span>
      {iconEnd ? <Glyph name={iconEnd} className="fl-btn__glyph" /> : null}
    </AriaButton>
  );
}

/**
 * React Aria says a busy button's state aloud through a hidden live region: an image node named by the
 * button (aria-labelledby its id) that stays for seven seconds. Once the button is gone, as when a
 * dialog closes on a save that landed, that node names nothing, and axe flags an image with no name
 * (role-img-alt). So the button removes the nodes that name it when it goes.
 */
export function dropAnnouncements(root: Pick<ParentNode, 'querySelectorAll'>, id: string): number {
  let dropped = 0;
  root.querySelectorAll('[data-live-announcer] [aria-labelledby]').forEach((n) => {
    if ((n.getAttribute('aria-labelledby') ?? '').split(' ').includes(id)) {
      n.remove();
      dropped++;
    }
  });
  return dropped;
}
